import { defineMessages } from '@/shared/i18n/define';
import { createDefaultPrefs } from './prefsDefaults';
import { widgetsDePerfil } from './perfilesWidgets';
import type { Lang, PerfilTipo, PreferenciasConfig } from './types';

export { WIDGET_CATALOGO } from './widgetCatalogo';
export type { ParamDef, WidgetDef } from './widgetCatalogo';

/** Ids de icono de dominio que representan a cada perfil (ver `components/icons`). */
export type PerfilIconId =
  'vaca' | 'leche' | 'queso' | 'cerdo' | 'bufalo' | 'hortaliza' | 'mercado';

export const perfilesMsgs = defineMessages(
  'perfiles',
  {
    ganadero_carne: {
      nombre: 'Ganadero de carne',
      descripcion: 'Toros, vacas, mautes y novillos: precio en pie y en canal.',
    },
    lechero: {
      nombre: 'Productor de leche',
      descripcion: 'Leche fría y caliente, y la vaca de descarte.',
    },
    quesero: {
      nombre: 'Quesero / lechero',
      descripcion: 'Queso y leche para queseras: lo que pagas y lo que cobras.',
    },
    porcicultor: {
      nombre: 'Porcicultor',
      descripcion: 'Cerdo industrial, cerda y lechón, en pie y en canal.',
    },
    bufalero: {
      nombre: 'Productor de búfalos',
      descripcion: 'Búfalos y búfalas en pie y en canal, y la leche de búfala.',
    },
    agricultor: {
      nombre: 'Productor de hortalizas y frutas',
      descripcion: 'Hortalizas y frutas al precio del productor.',
    },
    comerciante: {
      nombre: 'Comprador / comerciante',
      descripcion: 'Canal de res y cerdo, y productor contra consumidor en hortalizas y queso.',
    },
  },
  {
    ganadero_carne: {
      nombre: 'Beef rancher',
      descripcion: 'Bulls, cows, young bulls and steers: live and carcass prices.',
    },
    lechero: {
      nombre: 'Dairy producer',
      descripcion: 'Cold and fresh milk, and cull cows.',
    },
    quesero: {
      nombre: 'Cheesemaker / dairyman',
      descripcion: 'Cheese and milk for cheese plants: what you pay and what you charge.',
    },
    porcicultor: {
      nombre: 'Pig farmer',
      descripcion: 'Industrial pig, sow and piglet, live and carcass.',
    },
    bufalero: {
      nombre: 'Buffalo producer',
      descripcion: 'Buffalo bulls and cows, live and carcass, and buffalo milk.',
    },
    agricultor: {
      nombre: 'Vegetable and fruit grower',
      descripcion: 'Vegetables and fruit at the farm-gate price.',
    },
    comerciante: {
      nombre: 'Buyer / trader',
      descripcion: 'Beef and pork carcass, and farm-gate against retail for vegetables and cheese.',
    },
  },
);

export interface PerfilDef {
  clave: PerfilTipo;
  iconId: PerfilIconId;
  nombre: Record<Lang, string>;
  descripcion: Record<Lang, string>;
  /** Rubros que le interesan (para ordenar y resaltar). */
  rubrosFoco: readonly string[];
  /** Lista blanca de rubros por defecto en `intereses` (vacía = todo). */
  interesesRubros: readonly string[];
  /** Rendimiento en canal por defecto para este perfil (% del peso en pie). */
  rendimientoCanalPct: number;
}

const def = (
  clave: PerfilTipo,
  iconId: PerfilIconId,
  rubrosFoco: readonly string[],
  rendimientoCanalPct = 52,
  interesesRubros: readonly string[] = rubrosFoco,
): PerfilDef => ({
  clave,
  iconId,
  nombre: { es: perfilesMsgs.es[clave].nombre, en: perfilesMsgs.en[clave].nombre },
  descripcion: { es: perfilesMsgs.es[clave].descripcion, en: perfilesMsgs.en[clave].descripcion },
  rubrosFoco,
  interesesRubros,
  rendimientoCanalPct,
});

export const PERFILES: readonly PerfilDef[] = [
  def('ganadero_carne', 'vaca', ['bovino']),
  def('lechero', 'leche', ['lacteos', 'bovino'], 50),
  def('quesero', 'queso', ['lacteos'], 50),
  def('porcicultor', 'cerdo', ['porcino'], 76),
  def('bufalero', 'bufalo', ['bufalino', 'lacteos'], 50),
  def('agricultor', 'hortaliza', ['hortalizas', 'frutas']),
  def('comerciante', 'mercado', ['bovino', 'porcino', 'hortalizas', 'lacteos'], 52, []),
];

export const perfilDef = (perfil: PerfilTipo): PerfilDef =>
  PERFILES.find((p) => p.clave === perfil) ?? (PERFILES[0] as PerfilDef);

export const isPerfilTipo = (valor: unknown): valor is PerfilTipo =>
  PERFILES.some((p) => p.clave === valor);

/** Preferencias completas para un perfil: widgets, intereses y rendimiento (el resto, valores por defecto). */
export function configParaPerfil(perfil: PerfilTipo): PreferenciasConfig {
  const p = perfilDef(perfil);
  return {
    ...createDefaultPrefs(),
    perfilAplicado: perfil,
    rendimientoCanalPct: p.rendimientoCanalPct,
    home: { widgets: widgetsDePerfil(perfil) },
    intereses: { rubros: [...p.interesesRubros], regiones: [], fuentes: [] },
  };
}
